<template>
	<div class="seo__main">
		<HeaderBar
	    title="SEO"
	    :showButtons="false"
	    :ranges="ranges"
	    @change-range="onChangeRange"
	    @refresh="onRefresh"
	    :statistic="statistic"
	  />
	  <div class="seo-root" role="region" aria-label="SEO настройки">
	    <section class="seo-grid">
	      <div>
	        <label class="seo-label">Meta Title</label>
	        <input v-model="seo.meta_title" type="text" class="seo-input" :class="{'seo-input-error': errors.meta_title}" />
	        <div class="seo-hint">Длина: {{ seo.meta_title ? seo.meta_title.length : 0 }} / 70</div>
	        <div v-if="errors.meta_title" class="seo-error">{{ errors.meta_title }}</div>
	      </div>

	      <div>
	        <label class="seo-label">Canonical</label>
	        <input v-model="seo.canonical" type="text" class="seo-input" placeholder="https://example.com/landing-100" />
	      </div>

	      <div class="seo-full">
	        <label class="seo-label">Meta Description</label>
	        <textarea v-model="seo.meta_description" rows="4" class="seo-textarea" :class="{'seo-input-error': errors.meta_description}"></textarea>
	        <div class="seo-hint">Длина: {{ seo.meta_description ? seo.meta_description.length : 0 }} / 160</div>
	        <div v-if="errors.meta_description" class="seo-error">{{ errors.meta_description }}</div>
	      </div>

	      <div>
	        <label class="seo-label">Meta Keywords (через запятую)</label>
	        <input v-model="keywordsString" type="text" class="seo-input" placeholder="продукт X, купить X, решение Y" />
	      </div>

	      <div>
	        <label class="seo-label">Robots</label>
	        <select v-model="seo.robots" class="seo-select">
	          <option value="index,follow">index, follow</option>
	          <option value="noindex,follow">noindex, follow</option>
	          <option value="noindex,nofollow">noindex, nofollow</option>
	        </select>
	        <label class="seo-checkbox">
	          <input type="checkbox" v-model="seo.noindex_until_published" />
	          <span class="seo-checkbox-label">noindex пока не опубликовано</span>
	        </label>
	      </div>
	    </section>

	    <!-- Open Graph -->
	    <section class="seo-section">
	      <h3 class="seo-subtitle">Open Graph (соц. превью)</h3>
	      <div class="seo-grid">
	        <div>
	          <label class="seo-label">OG Title</label>
	          <input v-model="seo.open_graph['og:title']" type="text" class="seo-input" placeholder="Авто: meta_title" />
	        </div>
	        <div>
	          <label class="seo-label">OG Type</label>
	          <input v-model="seo.open_graph['og:type']" type="text" class="seo-input" placeholder="website" />
	        </div>
	        <div class="seo-full">
	          <label class="seo-label">OG Description</label>
	          <textarea v-model="seo.open_graph['og:description']" rows="3" class="seo-textarea"></textarea>
	        </div>

	        <div class="seo-full">
	          <label class="seo-label">OG Image</label>
	          <Upload
	            :initialFiles="ogInitialFiles"
	            @update:selectedFiles="onOgFilesUpdate"
	            :showDelete="true"
	            :showSelect="true"
	            :max-files="5"
	          />
	          <div v-if="seo.open_graph['og:image']" class="seo-og-preview">
	            <img :src="seo.open_graph['og:image']" alt="og image" class="seo-og-img" />
	            <div class="seo-og-text">Текущая og:image</div>
	          </div>
	        </div>
	      </div>
	    </section>

	    <!-- Twitter -->
	    <section class="seo-section">
	      <h3 class="seo-subtitle">Twitter</h3>
	      <div class="seo-grid">
	        <div>
	          <label class="seo-label">Card</label>
	          <input v-model="seo.twitter.card" type="text" class="seo-input" placeholder="summary_large_image" />
	        </div>
	        <div>
	          <label class="seo-label">Site (например @company)</label>
	          <input v-model="seo.twitter.site" type="text" class="seo-input" />
	        </div>
	        <div class="seo-full">
	          <label class="seo-label">Twitter Image (если хотите отдельно)</label>
	          <Upload
	            :initialFiles="twitterInitialFiles"
	            @update:selectedFiles="onTwitterFilesUpdate"
	            :showDelete="true"
	            :showSelect="true"
	            :max-files="3"
	          />
	        </div>
	      </div>
	    </section>

	    <!-- Structured data / hreflang / sitemap -->
	    <section class="seo-section">
	      <div class="seo-grid">
	        <div class="seo-full">
	          <label class="seo-label">Structured JSON-LD (включено: {{ seo.structured_data.enabled }})</label>
	          <textarea v-model="jsonLdString" rows="6" class="seo-textarea" placeholder='{"@context":"https://schema.org","@type":"WebPage"}'></textarea>
	          <div v-if="errors.json_ld" class="seo-error">{{ errors.json_ld }}</div>
	        </div>

	        <div>
	          <label class="seo-label">Hreflang</label>
	          <div class="hreflang-list">
	            <div v-for="(h, idx) in seo.hreflang" :key="idx" class="hreflang-row">
	              <input v-model="h.lang" class="seo-input small" placeholder="ru" />
	              <input v-model="h.url" class="seo-input" placeholder="https://..." />
	              <button @click.prevent="removeHreflang(idx)" class="seo-btn-link">Удалить</button>
	            </div>
	            <button @click.prevent="addHreflang" class="seo-btn">Добавить</button>
	          </div>

	          <div class="sitemap-block">
	            <label class="seo-checkbox">
	              <input type="checkbox" v-model="seo.sitemap.included" />
	              <span class="seo-checkbox-label">Включить в sitemap</span>
	            </label>
	            <div class="sitemap-priority">
	              <label class="seo-label small">Priority</label>
	              <input v-model.number="seo.sitemap.priority" type="number" step="0.1" min="0" max="1" class="seo-input tiny" />
	            </div>
	          </div>
	        </div>
	      </div>
	    </section>

	    <!-- Preview + Actions -->
	    <section class="seo-actions">
	      <div class="preview-box">
	        <div class="preview-hint">Превью (как в поисковой выдаче)</div>
	        <div class="preview-title">{{ seo.meta_title || '—' }}</div>
	        <div class="preview-domain">{{ displayDomain }}</div>
	        <div class="preview-desc">{{ seo.meta_description || '—' }}</div>
	      </div>

	      <div class="actions">
	        <button @click="saveSeo" class="seo-save">Сохранить</button>
	        <button @click="resetToStore" class="seo-cancel">Отменить</button>
	      </div>
	    </section>
	  </div>
	</div>
</template>

<script>
import { reactive, computed, watch, ref } from 'vue'
import { useStore } from 'vuex'
import Upload from "@/components/UI/Upload/Upload.vue";
import HeaderBar from './HeaderBar.vue'

export default {
  name: 'SEOSettings',
  components: { Upload, HeaderBar },
  setup() {
    const store = useStore()
    const landingSettings = computed(() => store.getters['landing/landingSettings'] || {})

    const defaultSeo = {
      meta_title: '',
      meta_description: '',
      meta_keywords: [],
      canonical: '',
      robots: 'index,follow',
      noindex_until_published: false,
      open_graph: { 'og:title': '', 'og:description': '', 'og:image': '', 'og:image:width': 1200, 'og:image:height': 630, 'og:type': 'website' },
      twitter: { card: 'summary_large_image', site: '', title: '', image: '' },
      structured_data: { enabled: false, json_ld: {} },
      hreflang: [],
      sitemap: { included: true, priority: 0.6, changefreq: 'weekly' },
      meta_robots_directives: { noarchive: false, nosnippet: false, noimageindex: false },
      updated_at: new Date().toISOString()
    }

    const seo = reactive(Object.assign({}, defaultSeo, JSON.parse(JSON.stringify(landingSettings.value.seo || {}))))


    watch(landingSettings, (ns) => {
		  const incoming = ns && ns.seo ? ns.seo : {}
		  Object.keys(defaultSeo).forEach(k => {
		    if (incoming[k] === undefined) {
		      // если в store нет — ставим дефолт, но не затираем уже отредактированное в компоненте
		      if (seo[k] === undefined) seo[k] = defaultSeo[k]
		    } else {
		      // заменяем целиком значение из store (глубокое клонирование чтобы не держать ссылки на vuex)
		      seo[k] = JSON.parse(JSON.stringify(incoming[k]))
		    }
		  })

		  // синхронизируем вспомогательные поля, которые связаны с input'ами (keywordsString, jsonLdString)
		  keywordsString.value = (seo.meta_keywords || []).join(', ')
		  jsonLdString.value = JSON.stringify(seo.structured_data?.json_ld || {}, null, 2)
		}, { deep: true })


    const errors = reactive({})

    const keywordsString = ref((seo.meta_keywords && seo.meta_keywords.join(', ')) || '')
    watch(keywordsString, (val) => {
      seo.meta_keywords = val ? val.split(',').map(s => s.trim()).filter(Boolean) : []
    })

    const jsonLdString = ref(JSON.stringify(seo.structured_data?.json_ld || {}, null, 2))
    watch(jsonLdString, (val) => {
      try {
        seo.structured_data.json_ld = val ? JSON.parse(val) : {}
        errors.json_ld = ''
      } catch (e) {
        errors.json_ld = 'Некорректный JSON'
      }
    })

    const ogInitialFiles = computed(() => {
      const url = seo.open_graph && seo.open_graph['og:image'] ? seo.open_graph['og:image'] : null
      return url ? [{ name: 'og-image', url }] : []
    })
    const twitterInitialFiles = computed(() => {
      const url = seo.twitter && seo.twitter.image ? seo.twitter.image : null
      return url ? [{ name: 'twitter-image', url }] : []
    })

    function onOgFilesUpdate(files) {
      const selected = (files || []).find(f => f.selected) || files[0]
      if (selected) seo.open_graph['og:image'] = selected.url
    }

    function onTwitterFilesUpdate(files) {
      const selected = (files || []).find(f => f.selected) || files[0]
      if (selected) seo.twitter.image = selected.url
    }

    function addHreflang() {
      seo.hreflang = seo.hreflang || []
      seo.hreflang.push({ lang: '', url: '' })
    }

    function removeHreflang(idx) {
      if (!Array.isArray(seo.hreflang)) return
      seo.hreflang.splice(idx, 1)
    }

    const displayDomain = computed(() => {
      try {
        const c = seo.canonical || landingSettings.value.url || ''
        if (!c) return ''
        const u = new URL(c)
        return u.hostname
      } catch (e) {
        return ''
      }
    })

    function validate() {
      errors.meta_title = ''
      errors.meta_description = ''
      if (seo.meta_title && seo.meta_title.length > 70) errors.meta_title = 'Слишком длинный заголовок (макс 70 символов)'
      if (seo.meta_description && seo.meta_description.length > 160) errors.meta_description = 'Слишком длинное описание (макс 160 символов)'
      try {
        if (seo.structured_data && seo.structured_data.enabled) {
          JSON.stringify(seo.structured_data.json_ld)
        }
        errors.json_ld = ''
      } catch (e) {
        errors.json_ld = 'JSON-LD не валид'
      }
      return !(errors.meta_title || errors.meta_description || errors.json_ld)
    }

    function resetToStore() {
      const incoming = landingSettings.value.seo || {}
      Object.keys(defaultSeo).forEach(k => { seo[k] = incoming[k] === undefined ? defaultSeo[k] : incoming[k] })
      keywordsString.value = (seo.meta_keywords || []).join(', ')
      jsonLdString.value = JSON.stringify(seo.structured_data?.json_ld || {}, null, 2)
      Object.keys(errors).forEach(k => errors[k] = '')
    }

    async function saveSeo() {
		  // автозаполнение OG/Twitter
		  if (!seo.open_graph['og:title'] && seo.meta_title) seo.open_graph['og:title'] = seo.meta_title
		  if (!seo.open_graph['og:description'] && seo.meta_description) seo.open_graph['og:description'] = seo.meta_description
		  if (!seo.twitter.title && seo.meta_title) seo.twitter.title = seo.meta_title

		  seo.updated_at = new Date().toISOString()

		  if (!validate()) return

		  // Синхронизируем ключевые слова из строки
		  seo.meta_keywords = (keywordsString.value && keywordsString.value.length)
		    ? keywordsString.value.split(',').map(s => s.trim()).filter(Boolean)
		    : []

		  // Глубокий клон/нормализация перед отправкой
		  const normalizedSeo = JSON.parse(JSON.stringify({
		    meta_title: seo.meta_title || '',
		    meta_description: seo.meta_description || '',
		    meta_keywords: seo.meta_keywords || [],
		    canonical: seo.canonical || '',
		    robots: seo.robots || 'index,follow',
		    noindex_until_published: !!seo.noindex_until_published,
		    open_graph: Object.assign({ 'og:title':'', 'og:description':'', 'og:image':'', 'og:image:width':1200, 'og:image:height':630, 'og:type':'website' }, seo.open_graph || {}),
		    twitter: Object.assign({ card:'summary_large_image', site:'', title:'', image:'' }, seo.twitter || {}),
		    structured_data: { enabled: !!(seo.structured_data && seo.structured_data.enabled), json_ld: seo.structured_data?.json_ld || {} },
		    hreflang: Array.isArray(seo.hreflang) ? seo.hreflang : [],
		    sitemap: Object.assign({ included:false, priority:0.6, changefreq:'weekly' }, seo.sitemap || {}),
		    meta_robots_directives: Object.assign({ noarchive:false, nosnippet:false, noimageindex:false }, seo.meta_robots_directives || {}),
		    updated_at: seo.updated_at || new Date().toISOString()
		  }))

		  try {
		    // 1) локально обновляем (чтобы UI сразу показывал изменения)
		    await store.dispatch('landing/UPDATE_LANDING_SETTINGS', { seo: normalizedSeo })

		    // 2) если settings не были сохранены на сервере — сначала создаём/обновляем settings (чтобы получить uid/id)
		    const isDefault = store.getters['landing/isDefaultSettings']
		    const localSettings = store.getters['landing/landingSettings'] || {}
		    if (isDefault || !localSettings.id || localSettings.id === '100') {
		      await store.dispatch('landing/uploadLandingSettingsToServer')
		    }

		    // 3) вызываем uploadSeoToServer и передаём нормализованный seo явно
		    await store.dispatch('landing/uploadSeoToServer', { updated_by: null, seo: normalizedSeo })

		    // опционально: toast об успехе
		    // emit('toast', { type: 'success', text: 'SEO сохранено' })
		  } catch (e) {
		    console.warn('Ошибка сохранения SEO', e)
		    // показать пользователю ошибку
		  }
		}



    return {
      seo,
      errors,
      keywordsString,
      jsonLdString,
      ogInitialFiles,
      twitterInitialFiles,
      onOgFilesUpdate,
      onTwitterFilesUpdate,
      addHreflang,
      removeHreflang,
      saveSeo,
      resetToStore,
      displayDomain
    }
  }
}
</script>

<style scoped>
.seo-root {
  color: var(--color-0); /* light text */
  padding: 1.25rem; /* 20px */
  border-radius: 0.5rem;
  font-size: 1rem; /* base */
  line-height: 1.4;
  background-color: var(--blue-3);
}
.seo-title { font-size: 1.125rem; font-weight: 600; margin-bottom: 0.75rem; }
.seo-subtitle { font-size: 1rem; font-weight: 500; margin-bottom: 0.5rem; }

/* grid */
.seo-grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0,1fr));
  gap: 0.75rem;
}
@media(min-width: 48rem) {
  .seo-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
}
.seo-full { grid-column: 1 / -1; }

/* labels */
.seo-label { display:block; font-size:0.875rem; margin-bottom:0.375rem; color:#cfe6ff }

/* inputs & textareas — no borders */
.seo-input,
.seo-textarea,
.seo-select {
  width:100%;
  padding: 0.5rem 0.75rem; /* rem-based */
  background: rgba(255,255,255,0.03);
  color: inherit;
  border: none; /* no borders */
  outline: none;
  border-radius: 0.375rem;
  font-size: 0.95rem;
}
.seo-input.small { padding: 0.35rem 0.5rem; font-size: 0.85rem }
.seo-input.tiny { width:6rem; padding:0.25rem }
.seo-textarea { min-height: 6rem; resize: vertical }
.seo-select { appearance: none; }
.seo-input:focus, .seo-textarea:focus, .seo-select:focus { box-shadow: 0 0 0 0.15rem rgba(66,153,225,0.08); }

/* hints and errors */
.seo-hint { font-size:0.8rem; color: #9fbbe6; margin-top:0.25rem }
.seo-error { font-size:0.85rem; color: #ffb4b4; margin-top:0.25rem }
.seo-input-error { box-shadow: 0 0 0 0.12rem rgba(255,80,80,0.15); }

/* Upload previews */
.seo-og-preview { margin-top:0.5rem; display:flex; align-items:center; gap:0.75rem }
.seo-og-img { width:8rem; height:5rem; object-fit:cover; border-radius:0.375rem }
.seo-og-text { font-size:0.875rem; color:#9fbbe6 }

/* hreflang */
.hreflang-list { display:flex; flex-direction:column; gap:0.5rem }
.hreflang-row { display:flex; gap:0.5rem; align-items:center }
.seo-btn { padding:0.4rem 0.6rem; background:transparent; color:#9fbbe6; border-radius:0.375rem }
.seo-btn-link { background:transparent; color:#ffb4b4; border:none; cursor:pointer }

/* Sitemap block */
.sitemap-block { margin-top:0.75rem }
.sitemap-priority { margin-top:0.5rem }

/* Actions & preview */
.seo-actions { display:flex; flex-direction:column; gap:0.75rem; margin-top:1rem }
@media(min-width:48rem) { .seo-actions { flex-direction:row; align-items:center; justify-content:space-between } }

.preview-box { background: rgba(255,255,255,0.02); padding:0.9rem; border-radius:0.5rem; width:100% }
.preview-hint { font-size:0.85rem; color:#9fbbe6; margin-bottom:0.25rem }
.preview-title { color:#66b2ff; font-weight:600; font-size:0.95rem }
.preview-domain { font-size:0.85rem; color:#9fbbe6; margin-top:0.2rem }
.preview-desc { margin-top:0.5rem; color:#d9edff }

.actions { display:flex; gap:0.5rem }
.seo-save { padding:0.6rem 1rem; background:#2563eb; color:white; border:none; border-radius:0.375rem; cursor:pointer }
.seo-cancel { padding:0.55rem 1rem; background:transparent; color:#9fbbe6; border:none; border-radius:0.375rem; cursor:pointer }

/* small accessibility tweaks */
input::placeholder, textarea::placeholder { color: rgba(230,238,248,0.45) }
.seo__main {
	padding: 1rem;
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 1rem
}
</style>
